import posthog, { type ConfigDefaults } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useRef } from "react";

import { useCookieConsent } from "@punto/frontend-shared/hooks/use-cookie-consent";

const PERSISTENCE_WITH_CONSENT = "localStorage+cookie" as const;
const PERSISTENCE_WITHOUT_CONSENT = "memory" as const;

const AnalyticsConsentHandler = () => {
  const { consentGiven } = useCookieConsent();
  const previousConsent = useRef<boolean | null>(null);

  useEffect(() => {
    if (previousConsent.current === consentGiven || consentGiven === null) {
      return;
    }
    previousConsent.current = consentGiven;

    if (consentGiven) {
      posthog.set_config({ persistence: PERSISTENCE_WITH_CONSENT });
      posthog.opt_in_capturing();
    } else {
      posthog.set_config({ persistence: PERSISTENCE_WITHOUT_CONSENT });
      posthog.opt_out_capturing();
    }
  }, [consentGiven]);

  return null;
};

interface AnalyticsProviderProps {
  children?: React.ReactNode;
  posthogKey?: string;
  posthogHost?: string;
  posthogDefaults?: ConfigDefaults;
}

export const AnalyticsProvider = ({
  children,
  posthogKey,
  posthogHost,
  posthogDefaults,
}: AnalyticsProviderProps) => {
  const { consentGiven } = useCookieConsent();

  if (!posthogKey) {
    return <>{children}</>;
  }

  const posthogOptions = {
    api_host: posthogHost,
    defaults: posthogDefaults,
    opt_out_capturing_by_default: true,
    persistence: consentGiven
      ? PERSISTENCE_WITH_CONSENT
      : PERSISTENCE_WITHOUT_CONSENT,
  };

  return (
    <PostHogProvider options={posthogOptions} apiKey={posthogKey}>
      <AnalyticsConsentHandler />
      {children}
    </PostHogProvider>
  );
};
